import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { UploadIcon } from '@paypalcorp/pp-react-icons';
import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE } from '../../utility/constants';
import getLocaleMessages from '../../buttons/utils/localesMessages';
import { Button } from '@paypalcorp/pp-react-buttons';
import { BodyText } from '@paypalcorp/pp-react-text';
import { trackEventWithData } from '../../buttons/utilsV3/analytics';
import { AddImageIcon, EditIcon } from '@paypalcorp/pp-react-icons';
import { Alert } from '@paypalcorp/pp-react-alert';
import _get from 'lodash/get';
import { getImageSize } from '../../buttons/utils/commonUtils';

const messages = getLocaleMessages('buttonsV3/dropZone');
const errors = getLocaleMessages('buttonsV3/fieldErrors');

const DropZoneComponent = ({
  fptiSpec,
  imgURL,
  onChange,
  imgWidth,
  showPreviewInDropzone,
  displayEditIcon,
  setDefaultHeight,
  showSolidBorder,
}) => {
  const [imageSrc, setImageSrc] = useState('');
  const [dropzoneError, setDropzoneError] = useState('');

  const resizeImage = (original, imgName, fileSize) => {
    const img = new Image();
    img.src = original;
    img.crossOrigin = 'Anonymous';
    img.onload = (el) => {
      let imgObj = el.target;
      let elem = document.createElement('canvas');
      elem.width = imgWidth;
      elem.height = (imgWidth * imgObj.height) / imgObj.width;

      let ctx = elem.getContext('2d');
      ctx.drawImage(el.target, 0, 0, elem.width, elem.height);
      const srcEncoded = ctx.canvas.toDataURL(el.target, 'image/jpeg', 0);
      setImageSrc(srcEncoded);

      const size = getImageSize(fileSize, messages);

      const image = {
        src: srcEncoded,
        name: messages.name
          ? messages.name
              .replace('{name}', imgName)
              .replace('{size}', size.size)
              .replace('{type}', size.type)
          : imgName,
      };

      onChange(image);
    };
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: ALLOWED_IMAGE_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    onDrop: useCallback((acceptedFiles) => {
      setDropzoneError('');
      acceptedFiles.map((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const binaryStr = reader.result;
          resizeImage(binaryStr, file.name, file.size);
        };
        reader.readAsDataURL(file);

        trackEventWithData(fptiSpec.updated, {
          file_format: file?.type,
        });
      });
    }, []),
    onDropRejected: (rejections) => {
      try {
        const errorCode = _get(rejections[0], 'errors[0].code', 'default');
        switch (errorCode) {
          case 'file-too-large':
            onChange({ hasError: true, error: errors.dropzone.maxSize });
            break;
          case 'file-invalid-type':
            onChange({ hasError: true, error: errors.dropzone.notSupported });
            break;
          default:
            onChange({ hasError: true, error: errors.dropzone.tryAgain });
            break;
        }
      } catch (e) {
        onChange({ hasError: true, error: errors.dropzone.tryAgain });
      }
    },
  });

  useEffect(() => {
    setImageSrc(imgURL);
  }, [imgURL]);

  const trackClickEvent = () => {
    trackEventWithData(fptiSpec.change);
  };

  return (
    <section className="dropzone-container">
      <div
        {...getRootProps({
          className: `dropzone ${
            showSolidBorder && imageSrc ? 'solid-border' : ''
          }`,
          tabIndex: `${imageSrc && showPreviewInDropzone ? '-1' : '0'}`,
        })}
        aria-hidden={imageSrc && showPreviewInDropzone}
      >
        {imageSrc && showPreviewInDropzone ? (
          <img
            src={imageSrc}
            className={`${setDefaultHeight ? 'default-height' : 'fit-image'}`}
            style={{ maxWidth: imgWidth }}
            aria-hidden="true"
          />
        ) : (
          <>
            <input id="dropzone-input" {...getInputProps()} />
            <UploadIcon size="xl2" />
            <BodyText> {messages.label} </BodyText>
          </>
        )}
      </div>

      {imageSrc && showPreviewInDropzone ? (
        <div {...getRootProps()}>
          <input type="file" id="upload" {...getInputProps()} />
          <Button
            className="button-link dropzone-button-link"
            for="upload"
            type="button"
            iconComponent={displayEditIcon ? EditIcon : AddImageIcon}
            onClick={trackClickEvent}
            tertiary
          >
            {displayEditIcon ? messages.editButton : messages.changeLogo}
          </Button>
        </div>
      ) : (
        <p>{messages.imgType}</p>
      )}
      {dropzoneError && (
        <Alert type="warning" className="dropzone-error">
          {dropzoneError}
        </Alert>
      )}
    </section>
  );
};

DropZoneComponent.propTypes = {
  fptiSpec: PropTypes.object,
  imgURL: PropTypes.string,
  onChange: PropTypes.func,
  imgWidth: PropTypes.number,
  displayEditIcon: PropTypes.bool,
  showPreviewInDropzone: PropTypes.bool,
  setDefaultHeight: PropTypes.bool,
  showSolidBorder: PropTypes.bool,
};

export default DropZoneComponent;
